import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type StatusTone =
  | "progress"
  | "success"
  | "warning"
  | "danger"
  | "neutral";

const TONE_CLASS: Record<StatusTone, string> = {
  progress:
    "border-status-progress/20 bg-status-progress-soft text-status-progress-foreground",
  success:
    "border-status-success/20 bg-status-success-soft text-status-success-foreground",
  warning:
    "border-status-warning/20 bg-status-warning-soft text-status-warning-foreground",
  danger:
    "border-status-danger/20 bg-status-danger-soft text-status-danger-foreground",
  neutral:
    "border-status-neutral/20 bg-status-neutral-soft text-status-neutral-foreground",
};

// Common trip/task/shipment/booking/operator statuses → tone. Keys are
// normalized to UPPER_SNAKE so "in_transit" and "InTransit" both resolve.
const STATUS_TONE: Record<string, StatusTone> = {
  // green — terminal-success / healthy
  ACTIVE: "success",
  COMPLETED: "success",
  DELIVERED: "success",
  DONE: "success",
  PAID: "success",
  APPROVED: "success",
  ACTIONED: "success",
  SUCCEEDED: "success",
  ENABLED: "success",
  ONLINE: "success",
  AVAILABLE: "success",
  IN_SERVICE: "success",
  ON_DUTY: "success",
  RECEIVED: "success",
  CONFIRMED: "success",
  HEALTHY: "success",
  ESTIMATES_ON: "success",
  // indigo — in-flight / assigned
  ASSIGNED: "progress",
  SELECTED: "progress",
  IN_PROGRESS: "progress",
  IN_TRANSIT: "progress",
  PICKED_UP: "progress",
  LABEL_READY: "progress",
  DROPPED_AT_CARRIER: "progress",
  AT_FACILITY: "progress",
  OUT_FOR_DELIVERY: "progress",
  PROCESSING: "progress",
  DISPATCHED: "progress",
  OPEN: "progress",
  DATE_SPECIFIC_RUNS: "progress",
  // amber — waiting / needs attention
  PLANNED: "warning",
  SCHEDULED: "warning",
  PENDING: "warning",
  PENDING_PAYMENT: "warning",
  PENDING_CANCELLATION: "warning",
  REFUND_APPROVAL: "warning",
  ESCALATED: "warning",
  PARTIALLY_APPROVED: "warning",
  AWAITING: "warning",
  REQUESTED: "warning",
  DRAFT: "warning",
  ON_HOLD: "warning",
  PAUSED: "warning",
  MAINTENANCE: "warning",
  ATTENTION: "warning",
  ESTIMATES_OFF: "warning",
  // red — terminal-failure / blocked
  CANCELLED: "danger",
  CANCELED: "danger",
  FAILED: "danger",
  OVERDUE: "danger",
  BLOCKED: "danger",
  REJECTED: "danger",
  DENIED: "danger",
  CANNOT_FULFILL: "danger",
  DECLINED: "danger",
  EXPIRED: "danger",
  OFFLINE: "danger",
  ERROR: "danger",
  EXCEPTION: "danger",
  OUT_OF_SERVICE: "danger",
  // gray — archived / disabled / neutral
  ARCHIVED: "neutral",
  DISABLED: "neutral",
  CLOSED: "neutral",
  INACTIVE: "neutral",
  OFF_DUTY: "neutral",
  REMOVED: "neutral",
  RETURNED: "neutral",
};

function normalize(status: string): string {
  return status
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toUpperCase();
}

export function statusTone(status: string): StatusTone {
  return STATUS_TONE[normalize(status)] ?? "neutral";
}

/** "IN_TRANSIT" → "In Transit", "active" → "Active". */
export function humanizeStatus(status: string): string {
  return normalize(status)
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export interface StatusBadgeProps {
  status: string;
  label?: string;
  className?: string;
}

/** Generic enum-status pill. Unknown values fall back to a neutral gray tone. */
export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  const tone = statusTone(status);
  return (
    <Badge
      variant="outline"
      data-status-tone={tone}
      className={cn(TONE_CLASS[tone], className)}
    >
      {label ?? humanizeStatus(status)}
    </Badge>
  );
}
